export type PromiseWorkerExecutor<T, R> = (data: MessageEvent<T>) => R;

type PromiseWorkerResolveCallback<O> = (message: O) => void;

type PromiseWorkerRejectCallback = (reason: any) => void;

export class WorkerPromise<T = any, O = any> {
  private _worker: Worker;
  private _resolves: PromiseWorkerResolveCallback<O>[] = [];
  private _rejects: PromiseWorkerRejectCallback[] = [];
  private readonly _executorUrl: string;

  constructor(executor: PromiseWorkerExecutor<T, O>, options?: WorkerOptions) {
    const executorStr = executor.toString();
    const executorStrMinified = executorStr.replace(/\s\s+/g, " ");
    const parsedExecutor = executorStrMinified.replace(/return (.+);.+$/s, (...args) => {
      return `postMessage(${args[1]}); }`;
    });

    const funcString = "onmessage = " + parsedExecutor;
    const funcBlob = new Blob([...funcString], { type: "text/plain" });
    const objUrl = URL.createObjectURL(funcBlob);

    this._executorUrl = objUrl;

    this._worker = new Worker(objUrl, options);
    this._worker.onmessage = (e) => this._sendMessage(e);
    this._worker.onerror = (e) => this._sendError(e);
  }

  public postMessage(data: T) {
    return new Promise<O>((resolve, reject) => {
      this._worker.postMessage(data);
      this._resolves.push(resolve);
      this._rejects.push(reject);
    });
  }

  public terminate() {
    this._worker.terminate();
    URL.revokeObjectURL(this._executorUrl);
  }

  private _sendMessage(e: MessageEvent) {
    this._resolves.shift()?.(e.data);
    this._rejects.shift();
  }

  private _sendError(reason: any) {
    this._resolves.shift();
    this._rejects.shift()?.(reason);
  }
}

export default WorkerPromise;
